import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Calendar as CalendarIcon, Eye } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function Snapshot() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/snapshots/public/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Snapshot not available"));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
        <Card className="p-8 max-w-md text-center" data-testid="snapshot-error">
          <h2 className="font-display text-xl font-semibold">Link unavailable</h2>
          <p className="text-sm text-zinc-500 mt-2">{error}</p>
        </Card>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Loading snapshot…</div>;
  const t = data.totals;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="snapshot-public-page">
      {/* Public header */}
      <header className="bg-zinc-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 flex items-center gap-3">
          <BrandLogo to={null} size="sm" testId="snapshot-brand" />
          <div>
            <div className="font-display font-bold text-lg leading-none">Brandon Wheelz</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mt-0.5">OPS Central · Public Snapshot</div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Snapshot</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1" data-testid="snap-title">{data.snapshot.title}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-zinc-500">
            <span className="capitalize"><strong>Scope:</strong> {data.snapshot.scope_type}{data.snapshot.scope_value ? ` · ${data.snapshot.scope_value}` : ""}</span>
            <span><CalendarIcon className="h-3 w-3 inline mr-1" />Generated {(data.snapshot.created_at || "").slice(0, 10)}</span>
            <span>Expires {(data.snapshot.expires_at || "").slice(0, 10)}</span>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="snap-kpis">
          <Tile label="Campaigns" value={t.total_campaigns} hint={`${t.active_campaigns} active`} />
          <Tile label="Installations" value={`${t.total_completed_installations}/${t.total_planned_installations}`} hint={`${t.completion_pct}% completed`} />
          <Tile label="At Risk" value={t.at_risk_count} accent="amber" />
          <Tile label="Delayed" value={t.delayed_count} accent="red" />
        </div>

        {/* Trend */}
        <Card className="p-5" data-testid="snap-trend">
          <h3 className="font-display text-lg font-semibold mb-1">Daily installations · last 14 days</h3>
          <p className="text-xs text-zinc-500 mb-4">Planned vs completed</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily_trend} margin={{ left: -10 }}>
                <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="planned" stroke="#71717a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="completed" stroke="#0055FF" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* At-risk list */}
        <Card className="overflow-hidden" data-testid="snap-at-risk">
          <div className="px-5 py-3 border-b border-zinc-200">
            <h3 className="font-display text-lg font-semibold">Campaigns needing attention</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
              <tr>
                <th className="text-left py-2.5 px-4 font-semibold">Campaign</th>
                <th className="text-left py-2.5 px-4 font-semibold">City</th>
                <th className="text-left py-2.5 px-4 font-semibold">Progress</th>
                <th className="text-left py-2.5 px-4 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.at_risk_campaigns.length === 0 && (
                <tr><td colSpan={4} className="text-center py-12 text-zinc-500 text-xs">All campaigns on track ✓</td></tr>
              )}
              {data.at_risk_campaigns.map((c) => (
                <tr key={c.campaign_id} className="border-b border-zinc-100">
                  <td className="py-2.5 px-4">
                    <div className="font-medium">{c.campaign_name}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{c.campaign_id}</div>
                  </td>
                  <td className="py-2.5 px-4 text-zinc-600">{c.city}</td>
                  <td className="py-2.5 px-4 font-mono text-xs">{c.completed}/{c.target}</td>
                  <td className="py-2.5 px-4"><StatusPill status={c.health} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <p className="text-xs text-zinc-400 text-center pt-4">
          <Eye className="h-3 w-3 inline mr-1" />
          You're viewing a public snapshot from BWZ-OPS Central. Real-time data is available only inside the platform.
        </p>
      </main>
    </div>
  );
}

function Tile({ label, value, hint, accent = "default" }) {
  const accentMap = {
    default: "text-zinc-950",
    amber: "text-amber-700",
    red: "text-red-700",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5">
      <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">{label}</p>
      <div className={`mt-2 text-3xl font-black tracking-tighter font-display ${accentMap[accent]}`}>{value}</div>
      {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
    </div>
  );
}
